import { Plus, X } from 'lucide-react';
import Button from '../../../components/Button';
import type { LearningLessonQuestionPage, QuestionOption } from '../services/learningTypes';
import BasePageEditor from './BasePageEditor';

interface QuestionPageEditorProps {
    page: LearningLessonQuestionPage;
    pageNumber: number;
    onUpdate: (field: keyof LearningLessonQuestionPage, value: string | number | boolean | QuestionOption[] | string[]) => void;
    onDelete: () => void;
    onSave?: () => Promise<void>;
    onConvertType?: () => void;
    saveStatus?: 'saved' | 'unsaved' | 'saving' | 'error';
}

export default function QuestionPageEditor({
    page,
    pageNumber,
    onUpdate,
    onDelete,
    onSave,
    onConvertType,
    saveStatus
}: QuestionPageEditorProps) {

    const handleAddOption = () => {
        const newOption: QuestionOption = {
            uid: crypto.randomUUID(),
            text: '',
            aiFriendlyText: ''
        };
        const newOptions = [...page.options, newOption];
        onUpdate('options', newOptions);
    };

    const handleUpdateOption = (optionId: string, field: 'text' | 'aiFriendlyText', value: string) => {
        const newOptions = page.options.map(option =>
            option.uid === optionId ? { ...option, [field]: value } : option
        );
        onUpdate('options', newOptions);
    };

    const handleDeleteOption = (optionId: string) => {
        const newOptions = page.options.filter(option => option.uid !== optionId);
        // Remove from correct answers if it was selected
        const newCorrectIds = page.correctOptionIds.filter(id => id !== optionId);
        onUpdate('options', newOptions);
        onUpdate('correctOptionIds', newCorrectIds);
    };

    const handleToggleCorrectAnswer = (optionId: string) => {
        const isCurrentlyCorrect = page.correctOptionIds.includes(optionId);
        let newCorrectIds: string[];

        if (isCurrentlyCorrect) {
            newCorrectIds = page.correctOptionIds.filter(id => id !== optionId);
        } else {
            newCorrectIds = [...page.correctOptionIds, optionId];
        }

        onUpdate('correctOptionIds', newCorrectIds);
    };

    const questionSpecificFields = (
        <div>
            {/* Should Save Answer Toggle */}
            <div className="mb-6">
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="shouldSaveAnswer"
                        checked={page.shouldSaveAnswer}
                        onChange={(e) => onUpdate('shouldSaveAnswer', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="shouldSaveAnswer" className="ml-2 text-sm font-medium text-gray-700">
                        Save user answers for AI processing
                    </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                    When enabled, user answers will be saved and AI-friendly versions of the question and options must be provided.
                </p>
            </div>

            {/* AI-Friendly Question Field - Only show when shouldSaveAnswer is true */}
            {page.shouldSaveAnswer && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI-Friendly Question
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                        type="text"
                        value={page.aiFriendlyQuestion || ''}
                        onChange={(e) => onUpdate('aiFriendlyQuestion', e.target.value)}
                        placeholder="Simplified version of the question for AI processing"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required={page.shouldSaveAnswer}
                    />
                    {page.shouldSaveAnswer && !page.aiFriendlyQuestion?.trim() && (
                        <p className="mt-1 text-sm text-red-600">
                            AI-friendly question is required when saving answers is enabled.
                        </p>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                    Answer Options
                </label>
                <Button
                    type="button"
                    onClick={handleAddOption}
                    variant="success"
                    size="sm"
                    icon={<Plus size={16} />}
                >
                    Add Option
                </Button>
            </div>

            <div className="space-y-3">
                {page.options.map((option, index) => (
                    <div key={option.uid} className="p-3 border border-gray-200 rounded-md">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={page.correctOptionIds.includes(option.uid)}
                                    onChange={() => handleToggleCorrectAnswer(option.uid)}
                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 text-sm text-gray-600">
                                    Correct
                                </label>
                            </div>
                            <div className="flex-1 min-w-0">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Option {index + 1} - Display Text
                                </label>
                                <input
                                    type="text"
                                    value={option.text}
                                    onChange={(e) => handleUpdateOption(option.uid, 'text', e.target.value)}
                                    placeholder={`Option ${index + 1} display text`}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <Button
                                type="button"
                                onClick={() => handleDeleteOption(option.uid)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-800"
                                icon={<X size={16} />}
                            />
                        </div>
                        {/* AI-Friendly Text - Only show when shouldSaveAnswer is true */}
                        {page.shouldSaveAnswer && (
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    AI-Friendly Text
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={option.aiFriendlyText || ''}
                                    onChange={(e) => handleUpdateOption(option.uid, 'aiFriendlyText', e.target.value)}
                                    placeholder={`Simplified version for AI processing`}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required={page.shouldSaveAnswer}
                                />
                                {page.shouldSaveAnswer && !option.aiFriendlyText?.trim() && (
                                    <p className="mt-1 text-xs text-red-600">
                                        AI-friendly text is required when saving answers is enabled.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {page.options.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        <p>No answer options yet.</p>
                        <p className="text-sm">Click "Add Option" to create the first option.</p>
                    </div>
                )}
            </div>

            {/* Reaction fields */}
            <div className="mt-6 space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correct Answer Reaction
                        {page.correctOptionIds.length > 0 && (
                            <span className="text-red-500 ml-1">*</span>
                        )}
                    </label>
                    <input
                        type="text"
                        value={page.correctAnswerReaction || ''}
                        onChange={(e) => onUpdate('correctAnswerReaction', e.target.value)}
                        placeholder="Message shown when user selects correct answer(s)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Incorrect Answer Reaction
                        {page.correctOptionIds.length > 0 && (
                            <span className="text-red-500 ml-1">*</span>
                        )}
                    </label>
                    <input
                        type="text"
                        value={page.incorrectAnswerReaction || ''}
                        onChange={(e) => onUpdate('incorrectAnswerReaction', e.target.value)}
                        placeholder="Message shown when user selects incorrect answer(s)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                {page.correctOptionIds.length > 0 && (!page.correctAnswerReaction || !page.incorrectAnswerReaction) && (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800">
                            ⚠️ Both reaction messages are required when correct answers are marked.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <BasePageEditor
            page={page}
            pageNumber={pageNumber}
            pageType="Question Page"
            currentType="question"
            onUpdate={onUpdate}
            onDelete={onDelete}
            onSave={onSave}
            onConvertType={onConvertType}
            saveStatus={saveStatus}
            markdownPreviewProps={{
                isQuestionPage: true,
                options: page.options,
                correctOptionIds: page.correctOptionIds
            }}
            additionalFields={questionSpecificFields}
            editorRows={12}
        />
    );
} 